import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Layers } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

function InterestForm({ courses }) {
  const [form, setForm] = useState({ name: "", email: "", course_slug: "" });
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.course_slug) return toast.error("Please select a course.");
    setSubmitting(true);
    try {
      await api.post("/interest", form);
      toast.success("Thank you! We'll be in touch about this course.");
      setForm({ name: "", email: "", course_slug: "" });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="register-interest-btn"
          className="inline-flex items-center gap-2 border border-gold-500/40 px-7 py-3.5 text-sm text-white/85 hover:border-gold-400 hover:text-gold-300 transition-colors duration-300"
        >
          Register Interest
        </button>
      </DialogTrigger>
      <DialogContent className="bg-ink-paper border-gold-500/25 text-white">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-white">Register your interest</DialogTitle>
          <DialogDescription className="text-white/55 text-sm">Tell us which course you'd like and we'll reach out with details.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="interest-form">
          <Input
            data-testid="interest-name"
            placeholder="Full name"
            value={form.name}
            required
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
          />
          <Input
            data-testid="interest-email"
            type="email"
            placeholder="Email address"
            value={form.email}
            required
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
          />
          <select
            data-testid="interest-course"
            value={form.course_slug}
            onChange={(e) => setForm({ ...form, course_slug: e.target.value })}
            className="w-full bg-ink border border-gold-500/25 text-white/90 px-3 py-2.5 text-sm outline-none focus:border-gold-400"
          >
            <option value="">Select a course…</option>
            {courses.map((c) => (
              <option key={c.slug} value={c.slug}>{c.title}</option>
            ))}
          </select>
          <DialogFooter>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="interest-submit"
              className="bg-gold-500 text-ink hover:bg-gold-400 w-full font-semibold"
            >
              {submitting ? "Submitting…" : "Submit Interest"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Academy() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="academy-page">
      <PageHero
        overline="Judah AI Academy"
        title={<>Equipping leaders for the <span className="font-accent text-gold-gradient italic">digital age.</span></>}
        subtitle="Practical Artificial Intelligence knowledge for ministries, businesses, educators, entrepreneurs, and students — grounded in biblical ethics and integrity."
      />

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24">
        <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <Overline>Available Courses</Overline>
          <InterestForm courses={courses} />
        </Reveal>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {courses.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 0.07}>
              <Link
                to={`/academy/${c.slug}`}
                data-testid={`course-card-${c.slug}`}
                className="group flex h-full flex-col border border-gold-500/20 bg-ink-paper/40 hover:border-gold-400/60 transition-colors duration-500"
              >
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img src={c.image} alt={c.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
                  <span className="absolute left-4 top-4 bg-ink/80 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold-400 border border-gold-500/30">
                    {c.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="font-serif text-2xl text-white group-hover:text-gold-300 transition-colors">{c.title}</h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed flex-1">{c.tagline}</p>
                  <div className="mt-6 flex items-center gap-5 text-xs text-white/50">
                    <span className="flex items-center gap-1.5"><Clock size={13} className="text-gold-500" /> {c.duration}</span>
                    <span className="flex items-center gap-1.5"><Layers size={13} className="text-gold-500" /> {c.lesson_count} lessons</span>
                  </div>
                  <div className="mt-6 flex items-center justify-between border-t border-gold-500/15 pt-5">
                    <span className="font-serif text-2xl text-gold-400">${c.price}</span>
                    <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-white/70 group-hover:text-gold-400 group-hover:gap-2.5 transition-all duration-300">
                      View <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
